import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import TransactionsPage from "@/pages/TransactionsPage";
import AddEntryPage from "@/pages/AddEntryPage";
import MyEntriesPage from "@/pages/MyEntriesPage";
import CategoriesPage from "@/pages/CategoriesPage";
import BudgetsPage from "@/pages/BudgetsPage";
import UsersPage from "@/pages/UsersPage";
import AccountPage from "@/pages/AccountPage";
import ReportsPage from "@/pages/ReportsPage";
import BillsPage from "@/pages/BillsPage";
import ImportPage from "@/pages/ImportPage";
import SettingsPage from "@/pages/SettingsPage";
import ActivityPage from "@/pages/ActivityPage";
import ReviewPage from "@/pages/ReviewPage";
import InstallPage from "@/pages/InstallPage";
import { OfflineBanner } from "@/components/common/OfflineBanner";

const homeFor = (user) => (user?.role === "admin" ? "/dashboard" : "/add");

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center" data-testid="app-loading">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

function RequireAuth() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function RequireAdmin() {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/add" replace />;
  return <Outlet />;
}

function PublicOnly() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return <LoginPage />;
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={homeFor(user)} replace />;
}

export default function App() {
  return (
    <div className="App">
      <OfflineBanner />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly />} />
            <Route path="/install" element={<InstallPage />} />
            <Route element={<RequireAuth />}>
              <Route path="/add" element={<AddEntryPage />} />
              <Route path="/my-entries" element={<MyEntriesPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route element={<RequireAdmin />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/budgets" element={<BudgetsPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/bills" element={<BillsPage />} />
                <Route path="/import" element={<ImportPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/review" element={<ReviewPage />} />
              </Route>
              <Route path="*" element={<Home />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}
